import { jsxs, jsx } from 'react/jsx-runtime';
import { Box, font, themedColor, Flex, useDropdownA11y } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES, StatusDot, BottomSmallArrowIcon } from '@lemonade-hq/nuis';
import { useRef, useCallback, useContext } from 'react';
import styled, { css } from 'styled-components';
import { reset } from '../../../helpers/tracking.esm.js';
import { AnalyticsContext } from '../../../providers/AnalyticsProvider.esm.js';
import { DropDownMenu, DropDownRow } from './DropDownMenu.esm.js';
import { AccountDropdownOptionsSettings } from './shared.esm.js';

function _tagged_template_literal(strings, raw) {
    if (!raw) {
        raw = strings.slice(0);
    }
    return Object.freeze(Object.defineProperties(strings, {
        raw: {
            value: Object.freeze(raw)
        }
    }));
}
function _templateObject() {
    var data = _tagged_template_literal([
        "\n  ",
        ";\n  color: ",
        ";\n  border: none;\n  background: transparent;\n  position: relative;\n  cursor: pointer;\n"
    ]);
    _templateObject = function _templateObject() {
        return data;
    };
    return data;
}
function _templateObject1() {
    var data = _tagged_template_literal([
        ""
    ]);
    _templateObject1 = function _templateObject1() {
        return data;
    };
    return data;
}
function _templateObject2() {
    var data = _tagged_template_literal([
        ""
    ]);
    _templateObject2 = function _templateObject2() {
        return data;
    };
    return data;
}
function _templateObject3() {
    var data = _tagged_template_literal([
        "\n      ",
        " {\n          display: block;\n      }\n      ",
        " {\n          display: none;\n      }\n  "
    ]);
    _templateObject3 = function _templateObject3() {
        return data;
    };
    return data;
}
function _templateObject4() {
    var data = _tagged_template_literal([
        "\n  height: 21px;\n  line-height: 21px;\n  padding-top: 1px;\n  ",
        "\n  letter-spacing: normal;\n  text-align: left;\n  text-transform: uppercase;\n\n  ",
        " {\n    display: none;\n  }\n  ",
        " {\n    display: block;\n  }\n\n  ",
        "\n"
    ]);
    _templateObject4 = function _templateObject4() {
        return data;
    };
    return data;
}
function _templateObject5() {
    var data = _tagged_template_literal([
        "\n  font-size: 1rem;\n  margin-right: 6px;\n  width: 9px;\n  height: 9px;\n"
    ]);
    _templateObject5 = function _templateObject5() {
        return data;
    };
    return data;
}
function _templateObject6() {
    var data = _tagged_template_literal([
        "\n      transform: rotate(180deg) translate(0%, 10%);\n    "
    ]);
    _templateObject6 = function _templateObject6() {
        return data;
    };
    return data;
}
function _templateObject7() {
    var data = _tagged_template_literal([
        "\n  transition: transform 0.3s ease-in-out;\n  transform-origin: center center;\n  width: 8px;\n  height: 6px;\n\n  ",
        "\n"
    ]);
    _templateObject7 = function _templateObject7() {
        return data;
    };
    return data;
}
var StyledUserSettings = styled(Box).attrs({
    as: "button"
})(_templateObject(), font("main", {
    fontSize: "1.6rem",
    fontWeight: 400,
    lineHeight: "2rem"
}), themedColor("night"));
var Initials = styled.span(_templateObject1());
var FullName = styled.span(_templateObject2());
var AccountTitle = styled(Flex)(_templateObject4(), font("main", {
    fontSize: "1.4rem",
    lineHeight: 1.3,
    fontWeight: 700
}), Initials, FullName, MEDIA_QUERIES.mobile(_templateObject3(), Initials, FullName));
var UserStatusDot = styled(StatusDot)(_templateObject5());
var Caret = styled(Flex)(_templateObject7(), function(param) {
    var isOpen = param.isOpen;
    return isOpen && css(_templateObject6());
});
var AppHeaderDropdown = function(param) {
    var _param_firstName = param.firstName, firstName = _param_firstName === void 0 ? "" : _param_firstName, _param_lastName = param.lastName, lastName = _param_lastName === void 0 ? "" : _param_lastName, trackingContext = param.trackingContext, accountDropdownOptionsSettings = param.accountDropdownOptionsSettings;
    var settingsRef = useRef(null);
    var _useDropdownA11y = useDropdownA11y({
        node: settingsRef,
        supportKeyboard: true
    }), isOpen = _useDropdownA11y.isOpen, setIsOpen = _useDropdownA11y.setIsOpen;
    var toggle = useCallback(function() {
        return setIsOpen(function(o) {
            return !o;
        });
    }, [
        setIsOpen
    ]);
    var fullName = "".concat(firstName, " ").concat(lastName);
    var _firstName_trim_, _lastName_trim_;
    var initials = ((_firstName_trim_ = firstName.trim()[0]) !== null && _firstName_trim_ !== void 0 ? _firstName_trim_ : "") + ((_lastName_trim_ = lastName.trim()[0]) !== null && _lastName_trim_ !== void 0 ? _lastName_trim_ : "");
    var trackEvent = useContext(AnalyticsContext).trackEvent;
    var toggleEvent = useCallback(function() {
        trackEvent("web_account.user_name.clicked", {
            origin: trackingContext != null ? trackingContext : ""
        });
        toggle();
    }, [
        toggle,
        trackEvent,
        trackingContext
    ]);
    var handleOnClick = useCallback(function(event, eventKey) {
        event.stopPropagation();
        trackEvent(eventKey, {
            origin: trackingContext != null ? trackingContext : ""
        });
    }, [
        trackEvent,
        trackingContext
    ]);
    return /*#__PURE__*/ jsxs(StyledUserSettings, {
        "aria-expanded": isOpen,
        "aria-haspopup": "true",
        onClick: toggleEvent,
        ref: settingsRef,
        children: [
            /*#__PURE__*/ jsxs(Flex, {
                alignItems: "center",
                children: [
                    /*#__PURE__*/ jsxs(Flex, {
                        alignItems: "center",
                        mr: "5px",
                        children: [
                            /*#__PURE__*/ jsx(UserStatusDot, {
                                dotType: "active"
                            }),
                            /*#__PURE__*/ jsxs(AccountTitle, {
                                children: [
                                    /*#__PURE__*/ jsx(Initials, {
                                        children: initials
                                    }),
                                    /*#__PURE__*/ jsx(FullName, {
                                        children: fullName
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(Caret, {
                        isOpen: isOpen,
                        children: /*#__PURE__*/ jsx(BottomSmallArrowIcon, {})
                    })
                ]
            }),
            isOpen && /*#__PURE__*/ jsxs(DropDownMenu, {
                role: "menu",
                children: [
                    /*#__PURE__*/ jsx("li", {
                        role: "none",
                        children: /*#__PURE__*/ jsx("a", {
                            href: accountDropdownOptionsSettings[AccountDropdownOptionsSettings.MyAccount].url,
                            onClick: function(event) {
                                return handleOnClick(event, "web_account.my_account.clicked");
                            },
                            role: "menuitem",
                            children: /*#__PURE__*/ jsx(DropDownRow, {
                                children: accountDropdownOptionsSettings[AccountDropdownOptionsSettings.MyAccount].title
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx("li", {
                        role: "none",
                        children: /*#__PURE__*/ jsx("a", {
                            href: accountDropdownOptionsSettings[AccountDropdownOptionsSettings.Settings].url,
                            onClick: function(event) {
                                return handleOnClick(event, "web_account.settings.clicked");
                            },
                            role: "menuitem",
                            children: /*#__PURE__*/ jsx(DropDownRow, {
                                children: accountDropdownOptionsSettings[AccountDropdownOptionsSettings.Settings].title
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx("li", {
                        role: "none",
                        children: /*#__PURE__*/ jsx("a", {
                            href: accountDropdownOptionsSettings[AccountDropdownOptionsSettings.Logout].url,
                            onClick: function(event) {
                                handleOnClick(event, "web_account.logout.clicked");
                                reset();
                            },
                            role: "menuitem",
                            children: /*#__PURE__*/ jsx(DropDownRow, {
                                children: accountDropdownOptionsSettings[AccountDropdownOptionsSettings.Logout].title
                            })
                        })
                    })
                ]
            })
        ]
    });
};

export { AppHeaderDropdown };
